// src/modules/app-views/account/pages/Subscription/modals/CertificateUploadModal/Step2.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Descriptions, Tag, Alert, Space, Typography, Divider, Progress, Upload } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import dayjs from 'dayjs';
import { parseCnpjToDisplay } from 'utils/helpers';
import { Tooltip } from 'antd';

// *******************************************************************************
// HELPERS
// *******************************************************************************
const formatFileSize = bytes => {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb < 1 ? `${bytes}B` : `${kb.toFixed(1)}KB`;
};

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const SummaryStepContent = () => {
  const { form } = useSimpleFormContext();
  const certificateInfo = form.getFieldValue('certificateInfo');
  const certificateFile = form.getFieldValue('certificateFile');

  if (!certificateInfo) {
    return (
      <Alert
        title={translateX('error')}
        description={translateX('certificate_info_not_found')}
        type="error"
        showIcon
      />
    );
  }

  // File list for display (read-only, no remove option)
  const fileList = certificateFile?.name
    ? [
        {
          uid: '-1',
          name: `${certificateFile.name} (${formatFileSize(certificateFile.size)})`,
          status: 'done',
        },
      ]
    : [];

  // Calculate total days and elapsed days for progress
  const validFrom = dayjs(certificateInfo.validFrom);
  const expiresAt = dayjs(certificateInfo.expiresAt);
  const now = dayjs();

  const totalDays = expiresAt.diff(validFrom, 'day');
  const elapsedDays = now.diff(validFrom, 'day');

  // Progress percentage (0% = just created, 100% = expired)
  const progressPercent = Math.max(0, Math.min(100, (elapsedDays / totalDays) * 100));

  // Calculate gradient color based on progress (green -> orange -> red)
  const getProgressColor = percent => {
    if (percent <= 50) {
      // Green to Orange (0-50%)
      const ratio = percent / 50;
      const r = Math.round(82 + (250 - 82) * ratio); // 82 (green) -> 250 (orange)
      const g = Math.round(196 + (173 - 196) * ratio); // 196 (green) -> 173 (orange)
      const b = Math.round(26 + (20 - 26) * ratio); // 26 (green) -> 20 (orange)
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Orange to Red (50-100%)
      const ratio = (percent - 50) / 50;
      const r = Math.round(250 + (245 - 250) * ratio); // 250 (orange) -> 245 (red)
      const g = Math.round(173 + (34 - 173) * ratio); // 173 (orange) -> 34 (red)
      const b = Math.round(20 + (45 - 20) * ratio); // 20 (orange) -> 45 (red)
      return `rgb(${r}, ${g}, ${b})`;
    }
  };

  // Status configuration
  const statusConfig = {
    valid: {
      color: 'success',
      icon: <CheckCircleOutlined />,
    },
    expiring: {
      color: 'warning',
      icon: <WarningOutlined />,
    },
    expired: {
      color: 'error',
      icon: <CloseCircleOutlined />,
    },
  };

  const status = statusConfig[certificateInfo.status] || statusConfig.valid;

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="middle">
      {/* Display attached file (read-only) */}
      <Upload
        fileList={fileList}
        listType="picture"
        className="certificate-upload-modal"
        showUploadList={{ showRemoveIcon: false }}
        iconRender={() => <SafetyCertificateOutlined style={{ fontSize: 24, color: '#52c41a' }} />}
      />

      {/* Expiry Progress - inverted (increases as time passes) */}
      <div style={{ padding: '0 8px' }}>
        <Tooltip title={translateX('days_until_expiry')}>
          <Progress
            percent={progressPercent}
            format={() => (
              <Space size={4}>
                {status.icon}
                <span>
                  {certificateInfo.daysUntilExpiry} {translateX('days')}
                </span>
              </Space>
            )}
            strokeColor={getProgressColor(progressPercent)}
            size={['100%', 16]}
          />
        </Tooltip>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* Certificate Details */}
      <Descriptions
        column={1}
        size="small"
        bordered
        styles={{ label: { width: 80, fontWeight: 500, textAlign: 'left' } }}
      >
        {/* Holder Name */}
        <Descriptions.Item label={translateX('holder')}>
          {certificateInfo.holderName}
        </Descriptions.Item>

        {/* CNPJ */}
        <Descriptions.Item label="CNPJ">
          {certificateInfo?.cnpj ? parseCnpjToDisplay(certificateInfo.cnpj) : '-'}
        </Descriptions.Item>

        {/* Serial Number */}
        <Descriptions.Item label={translateX('serial')}>
          {certificateInfo.serialNumber}
        </Descriptions.Item>

        {/* Valid From */}
        <Descriptions.Item label={translateX('issuance')}>
          {dayjs(certificateInfo.validFrom).format('DD/MM/YYYY HH:mm')}
        </Descriptions.Item>

        {/* Expires At */}
        <Descriptions.Item label={translateX('expiration')}>
          <Space>
            {dayjs(certificateInfo.expiresAt).format('DD/MM/YYYY HH:mm')}
            <Tag color={status.color}>
              {certificateInfo.daysUntilExpiry} {translateX('day_s')}
            </Tag>
          </Space>
        </Descriptions.Item>

        {/* Issuer */}
        <Descriptions.Item label={translateX('issuer')}>{certificateInfo.issuer}</Descriptions.Item>
      </Descriptions>
    </Space>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const summaryStep = () => ({
  stepTitle: 'confirm',
  stepTooltip: 'review_and_confirm',
  content: <SummaryStepContent />,
  validationFields: [],
  nextButtonText: 'save_certificate',
  nextButtonIcon: <SafetyCertificateOutlined />,
  isConfirmStep: true,
});

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default summaryStep;
